import io from 'socket.io-client';

function getSocketUrl() {
  const url = (process.env.REACT_APP_WEBSOCKET_URL || "").trim().replace(/\/+$/, "");
  if (url) return url;
  const apiUrl = (process.env.REACT_APP_API_URL || "").trim();
  if (apiUrl) return apiUrl.replace(/\/api\/?$/, "") || apiUrl;
  return "";
}

let socket = null;

export const connectWebSocket = (token) => {
  const baseUrl = getSocketUrl();
  const urlToUse = baseUrl || window.location.origin;
  if (!socket || !socket.connected) {
    socket = io(urlToUse, {
      path: "/socket.io",
      auth: token ? { token } : {},
      transports: ['polling', 'websocket'],
      timeout: 20000,
      forceNew: true,
      withCredentials: true
    });

    socket.on("connect", () => {
      attachTranscriptionProgressBridge(socket);
    });
    socket.on('disconnect', () => {});
    socket.on('connect_error', () => {});
    socket.on('error', () => {});
    attachTranscriptionProgressBridge(socket);
  }
  return socket;
};

export const disconnectWebSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

export const authenticateWebSocket = (userId, token) => {
  if (socket) {
    socket.emit('authenticate', { userId, token });
  }
};

/** Wait for socket to be connected and authenticated. Resolves true when server sends 'authenticated', false on error or timeout. Token is optional when auth uses httpOnly cookies. */
export const ensureWebSocketReady = (userId, token, timeoutMs = 10000) => {
  return new Promise((resolve) => {
    if (!userId) {
      resolve(false);
      return;
    }
    const s = connectWebSocket(token);
    if (!s) {
      resolve(false);
      return;
    }
    let settled = false;
    let t;
    const onAuth = () => done(true);
    const onAuthErr = () => {
      done(false);
    };
    const tryAuth = () => {
      s.emit('authenticate', { userId, token: token || '' });
    };
    let onConnect = () => {};
    let onErr = () => {};

    const done = (ok) => {
      if (settled) return;
      settled = true;
      s.off('connect', onConnect);
      s.off('connect_error', onErr);
      s.off('authenticated', onAuth);
      s.off('authentication_error', onAuthErr);
      if (t) clearTimeout(t);
      resolve(ok);
    };

    s.once('authenticated', onAuth);
    s.once('authentication_error', onAuthErr);

    if (s.connected) {
      tryAuth();
      t = setTimeout(() => {
        done(false);
      }, 5000);
      return;
    }

    onConnect = () => tryAuth();
    onErr = () => {
      done(false);
    };
    s.once('connect', onConnect);
    s.once('connect_error', onErr);
    t = setTimeout(() => {
      done(false);
    }, timeoutMs);
  });
};

export const joinMagicEditorRoom = (soapNoteId, userId) => {
  if (socket) {
    socket.emit('join_magic_editor', { soapNoteId, userId });
  }
};

export const listenForMagicEditorUpdates = (callback) => {
  if (socket) {
    socket.on('magic_editor_status', (data) => {
      callback({ type: 'status', ...data });
    });

    socket.on('magic_editor_progress', (data) => {
      callback({ type: 'progress', ...data });
    });

    socket.on('magic_editor_complete', (data) => {
      callback({ type: 'complete', ...data });
    });

    socket.on('magic_editor_error', (data) => {
      callback({ type: 'error', ...data });
    });
  }
};

export const removeMagicEditorListeners = () => {
  if (socket) {
    socket.off('magic_editor_status');
    socket.off('magic_editor_progress');
    socket.off('magic_editor_complete');
    socket.off('magic_editor_error');
  }
};

/**
 * Single stable socket listeners + mutable callback target so many parallel uploads
 * don't call socket.off() and drop transcription_complete for other jobs.
 */
const transcriptionSocketTarget = {
  onProgress: null,
  onComplete: null,
  onError: null,
  onStream: null,
};

/** Extra subscribers that always receive session-scoped job events (sidebar + SOAP tab). */
const transcriptionSessionEventListeners = new Set();
const jobStatusListeners = new Set();

function emitTranscriptionSessionEvent(type, data) {
  for (const listener of transcriptionSessionEventListeners) {
    try {
      listener(type, data);
    } catch (_) {
      /* ignore */
    }
  }
}

function bridgeTranscriptionProgress(data) {
  emitTranscriptionSessionEvent("progress", data);
  transcriptionSocketTarget.onProgress?.(data);
}
function bridgeTranscriptionComplete(data) {
  emitTranscriptionSessionEvent("complete", data);
  transcriptionSocketTarget.onComplete?.(data);
}
function bridgeTranscriptionError(data) {
  emitTranscriptionSessionEvent("error", data);
  transcriptionSocketTarget.onError?.(data);
}
function bridgeTranscriptionStream(data) {
  emitTranscriptionSessionEvent("stream", data);
  transcriptionSocketTarget.onStream?.(data);
}
function bridgeTranscriptionJobStatus(data) {
  emitTranscriptionSessionEvent("job_status", data);
  for (const callback of jobStatusListeners) {
    try {
      callback(data);
    } catch (_) {
      /* ignore */
    }
  }
}

function attachTranscriptionProgressBridge(s) {
  if (!s) return;
  s.off("transcription_progress", bridgeTranscriptionProgress);
  s.off("transcription_complete", bridgeTranscriptionComplete);
  s.off("transcription_error", bridgeTranscriptionError);
  s.off("transcription_stream", bridgeTranscriptionStream);
  s.off("transcription_job_status", bridgeTranscriptionJobStatus);
  s.on("transcription_progress", bridgeTranscriptionProgress);
  s.on("transcription_complete", bridgeTranscriptionComplete);
  s.on("transcription_error", bridgeTranscriptionError);
  s.on("transcription_stream", bridgeTranscriptionStream);
  s.on("transcription_job_status", bridgeTranscriptionJobStatus);
}

export const subscribeTranscriptionSessionEvents = (listener) => {
  if (typeof listener !== "function") return () => {};
  transcriptionSessionEventListeners.add(listener);
  const s = getSocket();
  if (s) attachTranscriptionProgressBridge(s);
  return () => transcriptionSessionEventListeners.delete(listener);
};

export const setTranscriptionSocketCallbacks = (callbacks = {}) => {
  transcriptionSocketTarget.onProgress = callbacks.onProgress || null;
  transcriptionSocketTarget.onComplete = callbacks.onComplete || null;
  transcriptionSocketTarget.onError = callbacks.onError || null;
  transcriptionSocketTarget.onStream = callbacks.onStream || null;
  const s = getSocket();
  if (s) attachTranscriptionProgressBridge(s);
};

export const clearTranscriptionSocketCallbacks = () => {
  transcriptionSocketTarget.onProgress = null;
  transcriptionSocketTarget.onComplete = null;
  transcriptionSocketTarget.onError = null;
  transcriptionSocketTarget.onStream = null;
};

export const listenForTranscriptionProgress = (callbacks) => {
  setTranscriptionSocketCallbacks(callbacks);
};

export const removeTranscriptionListeners = () => {
  clearTranscriptionSocketCallbacks();
};

export const listenForTranscriptionJobStatus = (callback) => {
  if (typeof callback !== "function") return;
  jobStatusListeners.add(callback);
  const s = getSocket();
  if (s) attachTranscriptionProgressBridge(s);
};

export const removeTranscriptionJobStatusListeners = (callback) => {
  if (typeof callback === "function") {
    jobStatusListeners.delete(callback);
    return;
  }
  jobStatusListeners.clear();
};

export const listenForTempAudioStatus = (callbacks) => {
  if (!socket) return;
  const { onCombineStart, onCombineDone } = callbacks || {};
  socket.off('temp_audio'); // avoid duplicate handlers
  socket.on('temp_audio', (data) => {
    if (data.subtype === 'combine_start' && onCombineStart) {
      onCombineStart(data);
    } else if (data.subtype === 'combine_done' && onCombineDone) {
      onCombineDone(data);
    }
  });
};

export const removeTempAudioStatusListeners = () => {
  if (socket) {
    socket.off('temp_audio');
  }
};

export const getSocket = () => socket;

export const isConnected = () => socket && socket.connected;